// Census Bureau API service for population density data
// Documentation: https://www.census.gov/data/developers/data-sets.html

export interface CensusTract {
  state: string;
  county: string;
  tract: string;
  population: number;
  area: number;
  density: number;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export interface PopulationData {
  tracts: CensusTract[];
  totalPopulation: number;
  averageDensity: number;
}

class CensusApiService {
  private baseUrl = 'https://api.census.gov/data';
  private year = '2020'; // Using 2020 decennial census data
  private dataset = 'dec/pl'; // Population and housing data

  /**
   * Get population data for Greater Omaha area including surrounding counties
   */
  async getOmahaPopulationData(): Promise<PopulationData> {
    try {
      const stateCode = '31'; // Nebraska
      
      // Define counties with more accurate bounds and known major cities
      const counties = [
        { 
          code: '055', 
          name: 'Douglas', 
          bounds: { north: 41.4, south: 41.1, east: -95.8, west: -96.2 },
          majorCities: ['Omaha', 'Elkhorn', 'Valley', 'Waterloo', 'Bennington']
        },
        { 
          code: '177', 
          name: 'Sarpy', 
          bounds: { north: 41.2, south: 40.9, east: -95.7, west: -96.1 },
          majorCities: ['Papillion', 'Bellevue', 'Gretna', 'La Vista', 'Ralston']
        },
        { 
          code: '153', 
          name: 'Washington', 
          bounds: { north: 41.5, south: 41.2, east: -96.0, west: -96.4 },
          majorCities: ['Blair', 'Fort Calhoun', 'Arlington', 'Herman']
        },
        { 
          code: '131', 
          name: 'Saunders', 
          bounds: { north: 41.6, south: 41.3, east: -96.2, west: -96.6 },
          majorCities: ['Wahoo', 'Ashland', 'Mead', 'Cedar Bluffs']
        },
        { 
          code: '067', 
          name: 'Dodge', 
          bounds: { north: 41.8, south: 41.5, east: -96.3, west: -96.7 },
          majorCities: ['Fremont', 'North Bend', 'Scribner', 'Hooper']
        },
        { 
          code: '053', 
          name: 'Cass', 
          bounds: { north: 41.0, south: 40.7, east: -95.9, west: -96.3 },
          majorCities: ['Plattsmouth', 'Weeping Water', 'Louisville', 'Eagle', 'Greenwood']
        },
      ];
      
      const allTracts: CensusTract[] = [];
      let totalPopulation = 0;
      
              // Fetch data for each county
        for (const county of counties) {
          // Get population data
          const populationUrl = `${this.baseUrl}/${this.year}/${this.dataset}?get=P1_001N,NAME&for=tract:*&in=state:${stateCode}&in=county:${county.code}`;
          
          console.log(`Fetching Census data for ${county.name} County:`, populationUrl);
          
          const response = await fetch(populationUrl);
          if (!response.ok) {
            console.warn(`Failed to fetch data for ${county.name} County: ${response.status}`);
            continue;
          }
          
          const data = await response.json();
          console.log(`Census API response for ${county.name}:`, data);
          
          // Calculate county area in square miles (approximate)
          const countyArea = this.getCountyArea(county.name);
          const tractCount = Math.max(1, data.length - 1); // Subtract header row
          const averageTractArea = countyArea / tractCount;
          
          // Skip the header row
          for (let i = 1; i < data.length; i++) {
            const row = data[i];
            const population = parseInt(row[0]) || 0;
            const tractCode = row[4];
            
            totalPopulation += population;
            
            // Calculate density using actual county area divided by tract count
            const density = population / averageTractArea;
            
            // Create more granular bounds for each tract
            const tractBounds = this.getTractBounds(county.bounds, i - 1, tractCount, county.name);
            
            allTracts.push({
              state: stateCode,
              county: county.code,
              tract: tractCode,
              population,
              area: averageTractArea,
              density,
              bounds: tractBounds
            });
          }
        }
      
      const averageDensity = totalPopulation / Math.max(1, allTracts.length);
      
      return {
        tracts: allTracts,
        totalPopulation,
        averageDensity
      };
      
    } catch (error) {
      console.error('Error fetching Census data:', error);
      throw error;
    }
  }

  /**
   * Get approximate county area in square miles
   * Source: US Census Bureau county area data
   */
  private getCountyArea(countyName: string): number {
    const countyAreas: { [key: string]: number } = {
      'Douglas': 331,    // Douglas County, NE - 331 sq mi
      'Sarpy': 241,      // Sarpy County, NE - 241 sq mi
      'Washington': 390, // Washington County, NE - 390 sq mi
      'Saunders': 754,   // Saunders County, NE - 754 sq mi
      'Dodge': 529,      // Dodge County, NE - 529 sq mi
      'Cass': 564,       // Cass County, NE - 564 sq mi
    };
    
    return countyAreas[countyName] || 300; // Default fallback
  }

  /**
   * Create more realistic tract bounds based on known city locations
   */
  private getTractBounds(countyBounds: any, tractIndex: number, totalTracts: number, countyName: string): any {
    // Define known city coordinates for better tract placement
    const cityCoordinates: { [key: string]: Array<{ lat: number; lng: number; name: string }> } = {
      'Douglas': [
        { lat: 41.2565, lng: -95.9345, name: 'Omaha' },
        { lat: 41.2861, lng: -96.2347, name: 'Elkhorn' },
        { lat: 41.3128, lng: -96.3506, name: 'Valley' },
        { lat: 41.2356, lng: -96.1856, name: 'Waterloo' },
        { lat: 41.3653, lng: -96.0814, name: 'Bennington' },
        { lat: 41.2033, lng: -96.1156, name: 'Millard' },
        { lat: 41.1833, lng: -96.1333, name: 'Chalco' },
        { lat: 41.2233, lng: -96.0833, name: 'West Omaha' },
        { lat: 41.2733, lng: -96.0533, name: 'Northwest Omaha' },
        { lat: 41.1933, lng: -96.0533, name: 'Southwest Omaha' }
      ],
      'Sarpy': [
        { lat: 41.1544, lng: -96.0425, name: 'Papillion' },
        { lat: 41.1367, lng: -95.8908, name: 'Bellevue' },
        { lat: 41.1408, lng: -96.2394, name: 'Gretna' },
        { lat: 41.1839, lng: -96.0319, name: 'La Vista' },
        { lat: 41.0586, lng: -96.0319, name: 'Ralston' }
      ],
      'Washington': [
        { lat: 41.5444, lng: -96.1250, name: 'Blair' },
        { lat: 41.4561, lng: -96.0250, name: 'Fort Calhoun' },
        { lat: 41.4533, lng: -96.3589, name: 'Arlington' },
        { lat: 41.6733, lng: -96.2239, name: 'Herman' }
      ],
      'Saunders': [
        { lat: 41.2114, lng: -96.6206, name: 'Wahoo' },
        { lat: 41.0394, lng: -96.3728, name: 'Ashland' },
        { lat: 41.2289, lng: -96.4894, name: 'Mead' },
        { lat: 41.3989, lng: -96.6117, name: 'Cedar Bluffs' }
      ],
      'Dodge': [
        { lat: 41.4333, lng: -96.5000, name: 'Fremont' },
        { lat: 41.4617, lng: -96.7797, name: 'North Bend' },
        { lat: 41.6667, lng: -96.6667, name: 'Scribner' },
        { lat: 41.6111, lng: -96.5486, name: 'Hooper' }
      ],
      'Cass': [
        { lat: 40.9989, lng: -95.8908, name: 'Plattsmouth' },
        { lat: 40.8700, lng: -96.1406, name: 'Weeping Water' },
        { lat: 40.9989, lng: -96.1622, name: 'Louisville' },
        { lat: 40.8167, lng: -96.4333, name: 'Eagle' },
        { lat: 40.9639, lng: -96.4411, name: 'Greenwood' }
      ]
    };

    const cities = cityCoordinates[countyName] || [];
    
    if (tractIndex < cities.length) {
      // Use actual city coordinates for the first few tracts
      const city = cities[tractIndex];
      const radius = 0.02; // Approximate tract radius in degrees
      
      return {
        north: city.lat + radius,
        south: city.lat - radius,
        east: city.lng + radius,
        west: city.lng - radius
      };
    } else {
      // For remaining tracts, create comprehensive grid coverage
      const latRange = countyBounds.north - countyBounds.south;
      const lngRange = countyBounds.east - countyBounds.west;
      
      // Create a grid pattern to ensure complete coverage
      const gridSize = Math.ceil(Math.sqrt(totalTracts - cities.length));
      const gridIndex = tractIndex - cities.length;
      const row = Math.floor(gridIndex / gridSize);
      const col = gridIndex % gridSize;
      
      const tractLatRange = latRange / gridSize;
      const tractLngRange = lngRange / gridSize;
      
      const lat = countyBounds.south + (row + 0.5) * tractLatRange;
      const lng = countyBounds.west + (col + 0.5) * tractLngRange;
      
      const tractRadius = Math.min(tractLatRange, tractLngRange) * 0.4; // Cover most of the grid cell
      
      return {
        north: Math.min(lat + tractRadius, countyBounds.north),
        south: Math.max(lat - tractRadius, countyBounds.south),
        east: Math.min(lng + tractRadius, countyBounds.east),
        west: Math.max(lng - tractRadius, countyBounds.west)
      };
    }
  }

  /**
   * Get tract boundaries for more accurate area calculations
   * This would require additional API calls to get geometry data
   */
  async getTractBoundaries(): Promise<any> {
    // This would require the TIGER/Line Shapefiles API
    // For now, we'll use estimated areas
    return null;
  }

  /**
   * Get population data for a specific area (by coordinates)
   */
  async getPopulationDataForArea(): Promise<PopulationData> {
    // For now, return Omaha area data
    // In a full implementation, we'd filter tracts by the bounding box
    return this.getOmahaPopulationData();
  }
}

export const censusApiService = new CensusApiService(); 